import { styled } from '@mui/material';
import round from 'lodash/round';

import { Icons, Typography, Stack, tokens } from '@cast/design-system';
import { ComputeResourceType } from '@cast/shared-types';

import { computeResourceUnitsMap } from 'constants/maps';
import {
  PreparedWorkload,
  ResourceMetrics,
} from 'features/workload-optimization';

const ArrowIcon = styled(Icons.ArrowRight)(({ theme }) => ({
  color: theme.palette.grey[400],
  fontSize: 16,
  pr: 2,
}));

const EqualsIcon = styled(Icons.Equals)(({ theme }) => ({
  color: theme.palette.grey[400],
  fontSize: 16,
  pr: 2,
}));

type ResourceMetricsProps = {
  metrics: ResourceMetrics;
  unit: ComputeResourceType;
};

type MetricValueProps = {
  value: number | undefined;
  testId: string;
};

const MetricValue = ({ value, testId }: MetricValueProps) => {
  return (
    <Typography
      variant="P12R"
      color={!value ? 'grey.400' : undefined}
      data-testid={testId}
      data-value={value ?? 0}
    >
      {value ? round(value, 3) : 'N/A'}
    </Typography>
  );
};

const RequestedMetricsRow = ({ metrics, unit }: ResourceMetricsProps) => {
  return (
    <MetricValue
      value={metrics.originalRequests}
      testId={`${unit}-originalRequests`}
    />
  );
};

export const OriginalRequestsCell = ({ cpu, memory }: PreparedWorkload) => {
  return (
    <Stack>
      <RequestedMetricsRow metrics={cpu} unit={ComputeResourceType.CPU} />
      <RequestedMetricsRow metrics={memory} unit={ComputeResourceType.MEMORY} />
    </Stack>
  );
};

const CurrentMetricsRow = ({ metrics, unit }: ResourceMetricsProps) => {
  const { originalRequests, requested } = metrics;
  const requestedRounded = requested && round(requested, 3);
  const originalRequestsRounded =
    originalRequests && round(originalRequests, 3);
  const isPositive =
    requestedRounded &&
    originalRequestsRounded &&
    requestedRounded === originalRequestsRounded;

  return (
    <Stack direction="row" gap={6} alignItems="center" data-testid={unit}>
      {isPositive ? (
        <EqualsIcon data-testid="positive-icon" />
      ) : (
        <ArrowIcon data-testid="negative-icon" />
      )}
      <MetricValue value={requested} testId={`${unit}-requests`} />
      <Typography variant="P12R" color={tokens.theme.fg.structural.secondary}>
        {computeResourceUnitsMap[unit]}
      </Typography>
    </Stack>
  );
};

export const CurrentRequestsCell = ({ cpu, memory }: PreparedWorkload) => {
  return (
    <Stack>
      <CurrentMetricsRow metrics={cpu} unit={ComputeResourceType.CPU} />
      <CurrentMetricsRow metrics={memory} unit={ComputeResourceType.MEMORY} />
    </Stack>
  );
};
